import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import Button from "../../../../components/ui/Button";
import { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import type {
  RecipientDataModalProps,
  RecipientFormData,
} from "../../types/recipientModal.types";
import { RecipientFormFields } from "./RecipientFormFields";
import { RetryTimer } from "./RetryTimer";
import { ModalFooter } from "./ModalFooter";
import CloseIcon from "../../../../components/icons/CloseIcon";
import { Spinner } from "../../../../components/ui/Spinner";
import { phoneValidationRequired } from "../../../../utils/validation";
import { observer } from "mobx-react-lite";
import { FormInputMask } from "../../../../components/ui/Form/FormInputMask";
import { toast } from "react-toastify";
import http from "../../../../libs/http";
import { Checkbox } from "../../../../components/ui/Checkbox";
import appStore from "../../../../stores/appStore";
import { isMobile } from "react-device-detect";

type AuthStage = "phone" | "code";

// @TODO: изменить время на 180
const RESEND_TIMEOUT = 180; // 3 минуты в секундах

const RecipientDataModal = ({
  isOpen,
  onClose,
  onSubmit,
  uniqueWarehousesCount,
  isLoading = false,
  userAuthState,
  userPhone = "",
  userName = "",
  userSurname = "",
  onAuthSuccess,
}: RecipientDataModalProps) => {
  const isAuthenticated = userAuthState === "authenticated";
  const isAuthenticatedNoName = userAuthState === "authenticated_no_name";
  const isNotAuthenticated = userAuthState === "not_authenticated";

  // Состояния для авторизации
  const [authStage, setAuthStage] = useState<AuthStage>("phone");
  const [phoneExists, setPhoneExists] = useState<boolean | null>(null);
  const [resendTimeout, setResendTimeout] = useState(0);
  const [authPhone, setAuthPhone] = useState("");
  const [codeError, setCodeError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
  const [showAccepted, setShowAccepted] = useState(true);

  const formSchema = yup.object().shape({
    firstName: yup.string().trim().required("Имя обязательно"),
    lastName: yup.string().trim().nullable().optional(),
    phone: phoneValidationRequired,
    verificationCode: isNotAuthenticated
      ? yup.string().required("Код обязателен")
      : yup.string().optional(),
    termsAccepted: isNotAuthenticated
      ? yup.boolean().oneOf([true], "Необходимо согласие с условиями")
      : yup.boolean().optional(),
    messagesAccepted: isNotAuthenticated
      ? yup.boolean().oneOf([true], "Необходимо согласие с условиями")
      : yup.boolean().optional(),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
    clearErrors,
    watch,
    trigger,
    setValue,
    getValues,
  } = useForm<RecipientFormData>({
    defaultValues: {
      phone: userPhone,
      firstName: userName,
      lastName: userSurname,
      termsAccepted: false,
      messagesAccepted: false,
    },
    resolver: yupResolver(formSchema),
    mode: "onSubmit",
  });

  const termsAccepted = watch("termsAccepted");
  const messagesAccepted = watch("messagesAccepted");
  const phoneInputValue = watch("phone");

  // Проверка существования телефона и согласия
  const checkPhoneExists = async (phoneNumber: string) => {
    try {
      const response = await http.post("/api/auth/check-phone", {
        phone: phoneNumber,
      });
      const exists: boolean = response?.data?.exists === true;
      const termsAccepted: boolean = response?.data?.terms_accepted === true;
      const messagesAccepted: boolean =
        response?.data?.messages_accepted === true;

      setPhoneExists(exists);

      // Для существующих пользователей проверяем, подтверждены ли согласия
      if (exists && (!termsAccepted || !messagesAccepted)) {
        // Пользователь существует, но согласия не подтверждены
        return { exists: true, consentRequired: true };
      } else if (exists && termsAccepted && messagesAccepted) {
        // Пользователь существует и согласия подтверждены
        return { exists: true, consentRequired: false };
      } else {
        // Пользователь не существует (нужны согласия при регистрации)
        return { exists: false, consentRequired: true };
      }
    } catch (error) {
      console.error("Ошибка проверки телефона", error);
      setPhoneExists(null);
      return null;
    }
  };

  // Отправка кода подтверждения
  const requestCode = async (phoneNumber: string) => {
    try {
      setAuthLoading(true);
      setCodeError("");

      await http.post("/api/auth/request-code", {
        phone: phoneNumber,
      });

      toast.success("Код отправлен на ваш номер", {
        position: "bottom-right",
        autoClose: 3000,
      });
      setAuthStage("code");
      setResendTimeout(RESEND_TIMEOUT);
    } catch (error) {
      toast.error("Ошибка при отправке кода", {
        position: "bottom-right",
        autoClose: 3000,
      });
      console.error(error);
    } finally {
      setAuthLoading(false);
    }
  };

  // Подтверждение кода
  const verifyCode = async (code: string) => {
    try {
      setAuthLoading(true);
      setCodeError("");
      clearErrors("verificationCode");
      const terms = watch('termsAccepted');
      const messages = watch('messagesAccepted');
      if(!terms || !messages) {
          trigger(['termsAccepted', 'messagesAccepted']);
          return;
      }
      const response = await http.post("/api/auth/verify-code", {
        phone: authPhone,
        code: code,
        terms_accepted: getValues("termsAccepted"),
        messages_accepted: getValues("messagesAccepted"),
      });

      if (onAuthSuccess) {
        await onAuthSuccess(response.data);
      }

      await appStore.getUser();
      toast.success("Успешная авторизация", {
        position: "bottom-right",
        autoClose: 3000,
      });

      // Переходим к обычному режиму формы
      setAuthStage("phone");
    } catch (error) {
      const errorMessage = "Неверный код";
      setCodeError(errorMessage);
      setError("verificationCode", {
        type: "manual",
        message: errorMessage,
      });
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
      });
      console.error("Ошибка верификации", error);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleFormSubmit = (data: RecipientFormData) => {
    onSubmit(data);
  };

  const handleVerificationConfirm = async () => {
    trigger("phone");
    if (authStage === "phone") {
      // Отправка кода
      const phoneData = getValues("phone");
      if (!phoneData || phoneData.length !== 18) {
        toast.error("Введите корректный номер телефона", {
          position: "bottom-right",
          autoClose: 3000,
        });
        return;
      }
      setAuthPhone(phoneData);
         const terms = getValues("termsAccepted");
      const messages = getValues("messagesAccepted");
      const phoneCheckResult = await checkPhoneExists(phoneData);
      console.log(phoneCheckResult);
      
      if (phoneCheckResult?.consentRequired && (!terms || !messages)) {
        setHasSubmitAttempt(true);
        // Принудительно валидируем чекбоксы
        // trigger(["termsAccepted", "messagesAccepted"]);
        // toast.error("Необходимо принять условия", {
        //   position: "bottom-right",
        //   autoClose: 3000,
        // });
        setShowAccepted(true);
      } else {
        setValue('messagesAccepted', true)
        setValue('termsAccepted', true)
        setShowAccepted(false);
      }
      await requestCode(phoneData);
    } else {
      // Подтверждение кода
      const codeData = getValues("verificationCode");
      if (!codeData || codeData.length !== 4) {
        toast.error("Введите 4-значный код", {
          position: "bottom-right",
          autoClose: 3000,
        });
        return;
      }

      // Проверяем согласия для новых пользователей или пользователей без согласия
      const terms = getValues("termsAccepted");
      const messages = getValues("messagesAccepted");
      const phoneCheckResult = await checkPhoneExists(authPhone);
      console.log(phoneCheckResult);
      
      if (phoneCheckResult?.consentRequired && (!terms || !messages)) {
        setHasSubmitAttempt(true);
        // Принудительно валидируем чекбоксы
        // trigger(["termsAccepted", "messagesAccepted"]);
        // toast.error("Необходимо принять условия", {
        //   position: "bottom-right",
        //   autoClose: 3000,
        // });
        setShowAccepted(true);
      } else {
        setValue('messagesAccepted', true)
        setValue('termsAccepted', true)
        setShowAccepted(false);
      }

      await verifyCode(codeData);
    }
  };

  const handleRetry = async () => {
    if (resendTimeout === 180 && phoneInputValue) {
      await requestCode(phoneInputValue);
    }
  };

  // Сброс состояния при закрытии модального окна
  useEffect(() => {
    if (!isOpen) {
      setAuthStage("phone");
      setCodeError("");
      setResendTimeout(0);
      setPhoneExists(null);
      setAuthPhone("");
      setAuthLoading(false);
      setHasSubmitAttempt(false);
    }
  }, [isOpen]);

  // Валидация формы при изменении чекбоксов (только после попытки отправки)
  useEffect(() => {
    if (authStage === "code" && hasSubmitAttempt && phoneExists === false) {
      // trigger(["termsAccepted", "messagesAccepted"]);
    }
  }, [
    termsAccepted,
    messagesAccepted,
    authStage,
    phoneExists,
    trigger,
    hasSubmitAttempt,
  ]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className={`w-full ${
                  isMobile ? "max-w-[95vw]" : "max-w-[max-content] !p-[30px]"
                } transform overflow-hidden rounded-[20px] bg-white ${
                  isMobile ? "p-4" : "p-[30px]"
                } text-left align-middle shadow-xl transition-all`}
              >
                {isLoading ? (
                  <div className="flex w-full items-center justify-center py-20">
                    <Spinner />
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit(handleFormSubmit)}
                    className={`flex flex-col ${
                      isMobile ? "gap-5" : "gap-[20px]"
                    }`}
                  >
                    {/* Header */}
                    <div
                      className={`flex flex-row justify-between items-start ${
                        isMobile ? "gap-3" : "gap-[20px]"
                      }`}
                    >
                      <div className="flex flex-col gap-[5px]">
                        <h2
                          className={`${isMobile ? "text-xl" : ""} modal-title`}
                        >
                          Данные получателя
                        </h2>
                        <p
                          className={`${
                            isMobile ? "text-sm" : ""
                          } modal-subtitle`}
                        >
                          По этим данным будет осуществляться выдача заказа и
                          информирование о его состоянии
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={onClose}
                        className={
                          " absolute !p-0 " + isMobile
                            ? "right-[15px] top-[15px] !p-0"
                            : "right-[30px] top-[30px]"
                        }
                      >
                        <CloseIcon
                          width={isMobile ? 27 : 32}
                          height={isMobile ? 27 : 32}
                          className="text-[#232323] hover:text-[#383181]"
                        />
                      </button>
                    </div>

                    {/* Form Fields */}
                    <div
                      className={`flex flex-col ${
                        isMobile ? "gap-4" : "gap-[20px]"
                      }`}
                    >
                      <RecipientFormFields
                        register={register}
                        errors={errors}
                        isPhoneDisabled={
                          isAuthenticated || isAuthenticatedNoName
                        }
                        defaultFirstName={userName}
                        defaultLastName={userSurname}
                      />
                      <div
                        className={`flex flex-1 ${
                          isMobile
                            ? "flex-col gap-4"
                            : "flex-row items-center gap-[20px]"
                        }`}
                      >
                        {isMobile ? (
                          <div className="flex flex-col gap-[15px]">
                            <div className="flex flex-row items-center gap-[10px]">
                              <FormInputMask
                                classNameForm={
                                  isMobile
                                    ? "min-w-[190px] w-full"
                                    : "w-[400px] "
                                }
                                className={isMobile ? "h-[50px]" : "h-[49px]"}
                                label="Телефон"
                                mask="+7 (000) 000-00-00"
                                placeholder="+7 (___) ___ - __ - __"
                                {...register("phone", {
                                  required: "Телефон обязателен",
                                })}
                                error={errors.phone?.message}
                                disabled={
                                  isAuthenticated || isAuthenticatedNoName || authStage === "code"
                                }
                              />
                              {authStage == "phone" && isNotAuthenticated && (
                                <Button
                                  type="button"
                                  style="outline"
                                  onClick={handleVerificationConfirm}
                                  isLoading={authLoading}
                                  className={`${
                                    isMobile
                                      ? "w-full  mt-[31px] h-[49px]"
                                      : "mt-[25px] min-w-[175px] h-[49px]"
                                  } !rounded-[15px]`}
                                >
                                  {authLoading
                                    ? "Отправка..."
                                    : authStage === "phone"
                                    ? "Получить код"
                                    : "Подтвердить"}
                                </Button>
                              )}
                            </div>
                            {isNotAuthenticated && (
                              <div className="flex flex-row gap-[10px]">
                                {authStage === "code" && (
                                  <div className={isMobile ? "w-full" : ""}>
                                    <div className="flex items-center">
                                      <div className={isMobile ? "flex-1" : ""}>
                                        <FormInputMask
                                          className={
                                            isMobile ? "h-[50px]" : "h-[49px]"
                                          }
                                          label="Код из СМС"
                                          {...register("verificationCode", {
                                            required: true,
                                            minLength: 4,
                                            maxLength: 4,
                                          })}
                                          mask="0000"
                                          placeholder={"Введите код"}
                                          error={
                                            errors.verificationCode?.message ||
                                            codeError
                                          }
                                          classNameForm={
                                            isMobile
                                              ? "w-full mr-3"
                                              : "w-[205px]"
                                          }
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {authStage == "code" && (
                                  <Button
                                    type="button"
                                    style="outline"
                                    onClick={handleVerificationConfirm}
                                    isLoading={authLoading}
                                    className={`${
                                      isMobile
                                        ? "min-w-[150px] mt-[33px] h-[49px]"
                                        : "mt-[25px] min-w-[175px] h-[49px]"
                                    } !rounded-[15px]`}
                                  >
                                    {authLoading
                                      ? "Отправка..."
                                      : "Подтвердить"}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            <FormInputMask
                              classNameForm={isMobile ? "w-full" : "w-[400px] "}
                              className={isMobile ? "h-[50px]" : "h-[49px]"}
                              label="Телефон"
                              mask="+7 (000) 000-00-00"
                              placeholder="+7 (___) ___ - __ - __"
                              {...register("phone", {
                                required: "Телефон обязателен",
                              })}
                              error={errors.phone?.message}
                              disabled={
                                isAuthenticated || isAuthenticatedNoName || authStage === "code"
                              }
                            />

                            {isNotAuthenticated && (
                              <>
                                {authStage === "code" && (
                                  <div className={isMobile ? "w-full" : ""}>
                                    <div className="flex items-center">
                                      <div className={isMobile ? "flex-1" : ""}>
                                        <FormInputMask
                                          className={
                                            isMobile ? "h-[50px]" : "h-[49px]"
                                          }
                                          label="Код из СМС"
                                          {...register("verificationCode", {
                                            required: true,
                                            minLength: 4,
                                            maxLength: 4,
                                          })}
                                          mask="0000"
                                          placeholder={"Введите код"}
                                          error={
                                            errors.verificationCode?.message ||
                                            codeError
                                          }
                                          classNameForm={
                                            isMobile
                                              ? "w-full mr-3"
                                              : "w-[205px]"
                                          }
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {!isMobile && isNotAuthenticated && (
                              <Button
                                type="button"
                                style="outline"
                                onClick={handleVerificationConfirm}
                                isLoading={authLoading}
                                className={`${
                                  isMobile
                                    ? "mt-0 w-full h-[50px]"
                                    : "mt-[32px] min-w-[175px] h-[49px]"
                                } !rounded-[15px]`}
                              >
                                {authLoading
                                  ? "Отправка..."
                                  : authStage === "phone"
                                  ? "Получить код"
                                  : "Подтвердить"}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                      {authStage === "code" && (
                        <RetryTimer
                          initialSeconds={resendTimeout}
                          onRetry={handleRetry}
                        />
                      )}
                    </div>

                    {/* Consent Checkboxes - Only for new users during code verification or existing users without consent */}
                    {isNotAuthenticated && authStage === "code" && showAccepted && (
                      <div
                        className={`flex flex-col gap-4 ${
                          isMobile ? "w-full" : "self-end w-[549px]"
                        }`}
                      >
                        <Checkbox
                          checked={termsAccepted || false}
                          onChange={(checked) => {
                            setValue("termsAccepted", checked);
                            if (hasSubmitAttempt) {
                              trigger("termsAccepted");
                            }
                          }}
                          label={
                            <span>
                              Согласие с{" "}
                              <a
                                href="/about/terms"
                                className="border-solid border-b-1"
                              >
                                условиями использования
                              </a>{" "}
                              и{" "}
                              <a
                                href="/about/privacy"
                                className="border-solid border-b-1"
                              >
                                политикой конфиденциальности
                              </a>
                            </span>
                          }
                          error={
                            hasSubmitAttempt
                              ? errors.termsAccepted?.message
                              : undefined
                          }
                        />
                        <Checkbox
                          checked={messagesAccepted || false}
                          onChange={(checked) => {
                            setValue("messagesAccepted", checked);
                            if (hasSubmitAttempt) {
                              trigger("messagesAccepted");
                            }
                          }}
                          label={"Согласие на получение сообщений от сервиса"}
                          error={
                            hasSubmitAttempt
                              ? errors.messagesAccepted?.message
                              : undefined
                          }
                        />
                      </div>
                    )}
                    {/* Footer */}
                    <ModalFooter
                      uniqueWarehousesCount={uniqueWarehousesCount}
                      isLoading={isLoading}
                      disabled={isLoading || !appStore.isLoginIn}
                    />
                  </form>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default observer(RecipientDataModal);
